import { FunctionComponent, useEffect, useState } from 'react';
import { Headline, Gutter } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import { GenericTable, OrderStatusIndicator, OrderStatusTranslations, CustomPaginator, PageChangeInfo } from '@cdaas/reusable-components-daas';
import { DateTime } from 'luxon';
import { useDispatch } from 'react-redux';
import { OrderLine, ProductOrder } from '@cdaas/api-clients/dist/order-openapi';
import { PageInfo } from '@cdaas/api-clients/dist/asset-user-openapi';

import { getMyOrders } from '../../utils';
import { showGrowlNotification } from '../../store/actions';
import OrderDetails from './OrderDetails';
import { DATE_AND_TIME_FORMAT, DEFAULT_PAGE_SIZE } from '../../utils/constants';

const Orders: FunctionComponent = () => {
  const [orders, setOrders] = useState<ProductOrder[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [pageInfo, setPageInfo] = useState<PageInfo>({ size: 0, total_items: 0, current_page: 0, total_pages: 0 });

  const dispatch = useDispatch();

  const ordersTranslations = useTranslation('orders').t;

  const orderStatusTranslations: OrderStatusTranslations = {
    approved: ordersTranslations('orderStatuses.approved'),
    processingOrder: ordersTranslations('orderStatuses.processingOrder'),
    delivered: ordersTranslations('orderStatuses.delivered'),
    pendingApproval: ordersTranslations('orderStatuses.pendingApproval'),
    financingDeclined: ordersTranslations('orderStatuses.financingDeclined'),
    approvalDeclined: ordersTranslations('orderStatuses.approvalDeclined'),
    cannotBeProcessed: ordersTranslations('orderStatuses.cannotBeProcessed'),
    cancelled: ordersTranslations('orderStatuses.cancelled'),
    returned: ordersTranslations('orderStatuses.returned'),
    discarded: ordersTranslations('orderStatuses.discarded'),
    draft: ordersTranslations('orderStatuses.draft'),
  };

  const getOrders = async (page: number) => {
    const ordersResponse = await getMyOrders(page, DEFAULT_PAGE_SIZE);
    if (ordersResponse) {
      setOrders(ordersResponse.contents);
      setPageInfo(ordersResponse.page_metadata);
    } else {
      dispatch(showGrowlNotification(ordersTranslations('getOrdersError'), 'warning'));
    }
    setLoading(false);
  };

  useEffect(() => {
    getOrders(pageInfo.current_page);
    // not adding used functions as dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (pageChangeInfo: PageChangeInfo<object>): void => {
    getOrders(pageChangeInfo.nextChosenPageNumber);
  };

  return (
    <>
      <Headline size="lg" margin={{ top: 'xlg', bottom: 'lg' }} padding={{ bottom: 'md' }}>
        {ordersTranslations('title')}
      </Headline>
      <GenericTable
        loading={loading ? 'short' : 'none'}
        noDataText={ordersTranslations('noOrdersText')}
        inputData={orders}
        rowConfigs={[
          {
            header: ordersTranslations('orderTableHeaders.date'),
            key: 'created',
            formatValue: date => DateTime.fromISO(date).toFormat(DATE_AND_TIME_FORMAT),
          },
          {
            header: ordersTranslations('orderTableHeaders.product'),
            key: 'packages',
            cellWidthMultiplier: 2,
            formatValue: packages => packages[0].order_lines.find((line: OrderLine) => line.is_main_product)?.name,
          },
          {
            header: ordersTranslations('orderTableHeaders.status'),
            key: 'status',
            cellWidthMultiplier: 2,
            formatValue: status => <OrderStatusIndicator status={status} translations={orderStatusTranslations} />,
          },
        ]}
        chevronConfig={{
          accordionContent: order => <OrderDetails order={order} />,
        }}
      />
      <Gutter size="xlg" />
      <CustomPaginator activatePageInfoObject={pageInfo} onPageChange={handlePageChange} />
    </>
  );
};

export default Orders;
